/**
 * <nbdy-shop-card> — three independent one-shot effects per card.
 *
 * 1. SCROLL-IN REVEAL (all hover modalities).
 *    On viewport entry (threshold 0.2), the card animates from
 *    `opacity: 0; translateY(20px) scale(0.98)` to its rest state over 700 ms
 *    with `cubic-bezier(0.22, 0.61, 0.36, 1)`. Cards in the same grid
 *    (closest `[data-nbdy-card-grid]` ancestor) stagger by 60 ms each based on
 *    DOM order so rows cascade left-to-right, top-to-bottom. Skipped when
 *    `prefers-reduced-motion: reduce`. Initial-state CSS is scoped to
 *    `:is(html.js)` (see `snippets/nbdy-shop-card-styles.liquid`) and this
 *    module flips that `js` class on the document element synchronously, so
 *    visitors without JS — or with this module blocked — never see the hidden
 *    initial state.
 *
 * 2. IMAGE-SWAP REVEAL (no-hover devices only).
 *    Same custom element observes itself a second time with threshold 0.4 and
 *    plays the secondary-image cross-fade once: wait 1000 ms → add
 *    `.nbdy-shop-card--show-secondary` → wait 1800 ms → remove. Desktop with
 *    `(hover: hover)` skips this entirely; pure CSS `:hover` drives the swap
 *    there.
 *
 * 3. TAP PREVIEW (no-hover devices only).
 *    When the user taps the card link on a no-hover device, intercepts the
 *    click, plays the same cross-fade for 350 ms (transition duration is
 *    overridden via `.nbdy-shop-card--tap-playing` so the fade completes in
 *    sync with the delay), then navigates to the product page via
 *    `window.location.assign`. Skipped on hover-capable devices (where the
 *    swap already happens on hover before any click) and on reduced motion.
 *    Modifier keys, middle-click, and `target=_blank` links bypass the
 *    intercept and navigate immediately. Any in-flight auto image-swap
 *    timers are cancelled to avoid a fade-back mid-tap.
 *
 * All three effects are one-shot per card per page-load.
 */

document.documentElement.classList.add('js');

const HAS_IO = 'IntersectionObserver' in window;
const HAS_WAAPI = typeof Element.prototype.animate === 'function';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const HOVER_CAPABLE = window.matchMedia('(hover: hover)').matches;

class NbdyShopCard extends HTMLElement {
  static SWAP_DELAY_MS = 1000;
  static SWAP_HOLD_MS = 1800;
  static SWAP_THRESHOLD = 0.4;

  static TAP_DURATION_MS = 350;

  static REVEAL_DURATION_MS = 700;
  static REVEAL_STAGGER_MS = 60;
  static REVEAL_THRESHOLD = 0.2;
  static REVEAL_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  static swapObserver = null;
  static swapObserverInitialized = false;

  static revealObserver = null;
  static revealObserverInitialized = false;

  static getSwapObserver() {
    if (this.swapObserverInitialized) return this.swapObserver;
    this.swapObserverInitialized = true;

    if (HOVER_CAPABLE || REDUCED_MOTION || !HAS_IO) return null;

    this.swapObserver = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.runSwap();
          obs.unobserve(entry.target);
        }
      },
      { threshold: this.SWAP_THRESHOLD }
    );
    return this.swapObserver;
  }

  static getRevealObserver() {
    if (this.revealObserverInitialized) return this.revealObserver;
    this.revealObserverInitialized = true;

    if (REDUCED_MOTION || !HAS_IO || !HAS_WAAPI) return null;

    this.revealObserver = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.runReveal();
          obs.unobserve(entry.target);
        }
      },
      { threshold: this.REVEAL_THRESHOLD }
    );
    return this.revealObserver;
  }

  connectedCallback() {
    const card = this.querySelector('.nbdy-shop-card');

    if (this.dataset.nbdySwapObserved !== 'true' && this.querySelector('.nbdy-shop-card__img-el--secondary')) {
      const obs = NbdyShopCard.getSwapObserver();
      if (obs) {
        this.dataset.nbdySwapObserved = 'true';
        obs.observe(this);
      }
    }

    if (this.dataset.nbdyRevealObserved !== 'true' && card) {
      const obs = NbdyShopCard.getRevealObserver();
      if (obs) {
        card.classList.add('nbdy-shop-card--reveal-pending');
        this.dataset.nbdyRevealObserved = 'true';
        obs.observe(this);
      }
    }

    if (this.dataset.nbdyTransitionBound !== 'true') {
      this.dataset.nbdyTransitionBound = 'true';
      this.addEventListener('click', this.handleViewTransition);
    }
  }

  handleViewTransition = (/** @type {MouseEvent} */ event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (target instanceof Element) {
      const interactive = target.closest('button, input, label, select, textarea, [role="button"]');
      if (interactive) return;
    }

    const imgWrap = this.querySelector('.nbdy-shop-card__img');
    if (!imgWrap) return;

    imgWrap.setAttribute('data-view-transition-type', 'product-image-transition');
    imgWrap.setAttribute('data-view-transition-triggered', 'true');

    if (HOVER_CAPABLE || REDUCED_MOTION) return;
    if (this.dataset.nbdyTapPlaying === 'true') return;
    if (!this.querySelector('.nbdy-shop-card__img-el--secondary')) return;

    const link = this.querySelector('.nbdy-shop-card');
    if (!(link instanceof HTMLAnchorElement) || !link.href) return;
    if (link.target && link.target !== '_self') return;

    event.preventDefault();
    this.runTap(link.href);
  };

  disconnectedCallback() {
    NbdyShopCard.swapObserver?.unobserve(this);
    NbdyShopCard.revealObserver?.unobserve(this);
  }

  runSwap() {
    if (this.dataset.nbdySwapPlayed === 'true') return;
    this.dataset.nbdySwapPlayed = 'true';
    const card = this.querySelector('.nbdy-shop-card');
    if (!card) return;
    this._swapDelayId = window.setTimeout(() => {
      this._swapDelayId = null;
      card.classList.add('nbdy-shop-card--show-secondary');
      this._swapHoldId = window.setTimeout(() => {
        this._swapHoldId = null;
        card.classList.remove('nbdy-shop-card--show-secondary');
      }, NbdyShopCard.SWAP_HOLD_MS);
    }, NbdyShopCard.SWAP_DELAY_MS);
  }

  runTap(/** @type {string} */ href) {
    this.dataset.nbdyTapPlaying = 'true';

    if (this._swapDelayId) window.clearTimeout(this._swapDelayId);
    if (this._swapHoldId) window.clearTimeout(this._swapHoldId);

    const card = this.querySelector('.nbdy-shop-card');
    if (!card) {
      window.location.assign(href);
      return;
    }

    card.classList.add('nbdy-shop-card--tap-playing', 'nbdy-shop-card--show-secondary');

    window.setTimeout(() => {
      window.location.assign(href);
    }, NbdyShopCard.TAP_DURATION_MS);
  }

  runReveal() {
    if (this.dataset.nbdyRevealPlayed === 'true') return;
    this.dataset.nbdyRevealPlayed = 'true';
    const card = this.querySelector('.nbdy-shop-card');
    if (!card) return;

    const grid = this.closest('[data-nbdy-card-grid]');
    const siblings = grid ? Array.from(grid.querySelectorAll('nbdy-shop-card')) : [];
    const index = siblings.indexOf(this);
    const delay = Math.max(0, index) * NbdyShopCard.REVEAL_STAGGER_MS;

    card.animate(
      [
        { opacity: 0, transform: 'translateY(20px) scale(0.98)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      {
        duration: NbdyShopCard.REVEAL_DURATION_MS,
        delay,
        easing: NbdyShopCard.REVEAL_EASING,
        fill: 'both',
      }
    );

    card.classList.remove('nbdy-shop-card--reveal-pending');
  }
}

if (!customElements.get('nbdy-shop-card')) {
  customElements.define('nbdy-shop-card', NbdyShopCard);
}
